import { router } from 'expo-router';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  FlatList,
  ListRenderItemInfo,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';

import {
  Carousel,
  CarouselPaging,
  CarouselRef,
  GenericCard,
  IconOffer,
  MqSwitch,
  PaginationDots,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useLoyaltyIncentives } from '../../queries/loyalty.queries';
import { formatOfferCards } from '../../utils';
import { OfferCard, OfferCardProps } from '../offer-card';
import { OfferCardSkeleton } from '../offer-card-skeleton';
import { SeeMoreCard } from '../see-more-card';

function renderOffer({ item }: ListRenderItemInfo<OfferCardProps>) {
  return <OfferCard {...item} />;
}

export interface OffersCarouselProps {
  /**
   * Maximum number of cards to render before showing "See All"
   */
  maxCards?: number;
  /**
   * Show the "See All" footer
   * @default true
   */
  showSeeAllFooter?: boolean;
  /**
   * Callback when "See All" is pressed
   */
  onPressSeeAll?: () => void;
}

export function OffersCarousel({
  maxCards,
  showSeeAllFooter = true,
  onPressSeeAll,
}: OffersCarouselProps) {
  const mqStyles = useMqStyles();
  const { formatMessage } = useIntl();
  const cardsToRender = useMqSelect({ $ltDesktop: maxCards ?? 5 }, maxCards ?? Infinity);
  const [carouselPaging, setCarouselPaging] = useState<CarouselPaging>();
  const carouselRef = useRef<CarouselRef>(null);

  const { loyaltyUserReady } = useLoyaltyLegacyStates();
  const { data, isLoading: isLoyaltyIncentivesLoading } = useLoyaltyIncentives();

  const isLoading = isLoyaltyIncentivesLoading || !loyaltyUserReady;

  const offerCardProps = useMemo(
    () => (isLoading ? [] : formatOfferCards(data?.offers, cardsToRender)),
    [data?.offers, cardsToRender, isLoading]
  );

  const goToPage = useCallback((page: number) => {
    carouselRef.current?.goToPage(page);
  }, []);

  const seeAllOffers = useCallback(() => {
    if (onPressSeeAll) {
      onPressSeeAll();
    } else {
      router.navigate('v2/offers/all');
    }
  }, [onPressSeeAll]);

  const renderOffersEmpty = useCallback(() => {
    if (isLoading) {
      return <OffersSkeleton style={mqStyles.emptyOffersContainer} />;
    }

    return (
      <GenericCard
        title={formatMessage({ id: 'noOffersAvailable' })}
        description={formatMessage({ id: 'noOffersAvailableMessage' })}
        hideButton
        icon={<IconOffer size={68} color={tokens.colors.$blackOpacity10} />}
        style={mqStyles.noOffersAvailable}
      />
    );
  }, [isLoading, mqStyles.noOffersAvailable, mqStyles.emptyOffersContainer, formatMessage]);

  const renderOffersFooter = useCallback(() => {
    if (!showSeeAllFooter || isLoading || offerCardProps.length === 0) {
      return null;
    }
    return (
      <SeeMoreCard
        style={mqStyles.seeMoreOffers}
        title={formatMessage(
          { id: 'lookingForMore' },
          { incentive: formatMessage({ id: 'offers' }) }
        )}
        buttonLabel={formatMessage({ id: 'seeAll' })}
        onPress={seeAllOffers}
      />
    );
  }, [
    formatMessage,
    mqStyles.seeMoreOffers,
    isLoading,
    seeAllOffers,
    offerCardProps.length,
    showSeeAllFooter,
  ]);

  const renderHeader = useCallback(() => {
    return <View style={mqStyles.listHeader} />;
  }, [mqStyles.listHeader]);

  const listHeader = useMqSelect({ $ltDesktop: renderHeader }, undefined);

  const renderCarouselLoading = useCallback(() => {
    return <OffersSkeleton />;
  }, []);

  const offerCardWidth = useMqSelect({ $gteDesktop: 215 }, 160);

  return (
    <MqSwitch
      $base={() => (
        <FlatList
          centerContent
          style={mqStyles.flatList}
          contentContainerStyle={mqStyles.offersContentFlatList}
          horizontal
          data={offerCardProps}
          renderItem={renderOffer}
          ListEmptyComponent={renderOffersEmpty}
          showsHorizontalScrollIndicator={false}
          ListFooterComponent={renderOffersFooter}
          ListHeaderComponent={listHeader}
        />
      )}
      $gteDesktop={() => (
        <>
          {offerCardProps.length === 0 ? (
            renderOffersEmpty()
          ) : (
            <View style={mqStyles.offersCarousel}>
              <Carousel
                renderCarouselLoading={renderCarouselLoading}
                ref={carouselRef}
                data={offerCardProps}
                renderItem={renderOffer}
                itemWidth={offerCardWidth}
                onChangePage={setCarouselPaging}
              />
              {carouselPaging && carouselPaging.totalPages > 1 && (
                <PaginationDots
                  style={mqStyles.carouselPagination}
                  selectedPage={carouselPaging.currentPage}
                  totalPages={carouselPaging.totalPages}
                  onPressPage={goToPage}
                />
              )}
            </View>
          )}
        </>
      )}
    />
  );
}

interface OffersSkeletonProps {
  style?: StyleProp<ViewStyle>;
}

function OffersSkeleton({ style }: OffersSkeletonProps) {
  const mqStyles = useSkeletonMqStyles();

  return (
    <ScrollView
      horizontal
      style={[mqStyles.emptyList, style]}
      contentContainerStyle={mqStyles.emptyListContent}
      showsHorizontalScrollIndicator={false}
    >
      <OfferCardSkeleton />
      <OfferCardSkeleton />
      <OfferCardSkeleton />
      <OfferCardSkeleton />
      <MqSwitch $ltDesktop={() => <OfferCardSkeleton />} />
    </ScrollView>
  );
}

const useSkeletonMqStyles = createMqStyles({
  emptyList: {
    $base: {
      flexGrow: 0,
    },
  },
  emptyListContent: {
    $base: {
      gap: 12,
    },
    $gteDesktop: {
      width: 908,
    },
  },
});

const useMqStyles = createMqStyles({
  flatList: {
    $base: {
      width: '100%',
      marginTop: 16,
      flexGrow: 0,
    },
  },
  offersCarousel: {
    $base: {
      marginTop: 16,
      width: '100%',
    },
  },
  carouselPagination: {
    $base: {
      alignSelf: 'center',
      marginTop: 16,
    },
  },
  offersContentFlatList: {
    $base: {
      gap: 16,
    },
    $ltDesktop: {
      gap: 12,
    },
  },
  seeMoreOffers: {
    $base: {
      height: 286,
      width: 215,
    },
    $ltDesktop: {
      width: 160,
      height: 232,
      marginRight: 16,
    },
  },
  listHeader: {
    $ltDesktop: {
      width: 4,
    },
  },
  emptyOffersContainer: {
    $gteDesktop: {
      marginTop: 16,
    },
  },
  noOffersAvailable: {
    $base: {
      maxWidth: 300,
      paddingVertical: 8,
    },
    $gteDesktop: {
      maxWidth: 600,
    },
  },
});
